import { t } from "i18next";
import { Eye } from "lucide-react";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { RowActionsMenu } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { PartyDto } from "@/features/kyc/api/dto";
import {
  KYC_STATUSES,
  PARTY_TYPES,
} from "@/features/shared/api/enums";

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

/**
 * Colonnes de la liste des identités (`POST /Parties/all`).
 * `PartyFilter` : `partyType`, `kycStatus` uniquement — les autres
 * colonnes n'ont pas de champ API, d'où `denied`.
 * Données PII en `NoTranslate` (règle notranslate).
 */
export const partiesColumns: ColumnsClarFactory<PartyDto> = (navigate) => [
  {
    id: "fullName",
    accessorFn: (row) =>
      [row.firstName, row.lastName].filter(Boolean).join(" ").trim(),
    header: t("kyc.parties.columns.fullName"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-medium">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "partyType",
    accessorKey: "partyType",
    header: t("kyc.parties.columns.partyType"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="secondary">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: { type: "select", options: selectOptions(PARTY_TYPES) },
  },
  {
    id: "phone",
    accessorKey: "phone",
    header: t("kyc.parties.columns.phone"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "email",
    accessorKey: "email",
    header: t("kyc.parties.columns.email"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "kycStatus",
    accessorKey: "kycStatus",
    header: t("kyc.parties.columns.kycStatus"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="outline">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: { type: "select", options: selectOptions(KYC_STATUSES) },
  },
  {
    id: "sanctionsScreeningStatus",
    accessorKey: "sanctionsScreeningStatus",
    header: t("kyc.parties.columns.sanctionsScreeningStatus"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="outline">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    // Pas dans PartyFilter.
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <RowActionsMenu
          actions={[
            {
              id: "view",
              label: t("kyc.parties.actions.view"),
              icon: Eye,
              onClick: () =>
                navigate(`/app/kyc/parties/${row.original.id}`),
            },
          ]}
        />
      ) : null,
    meta: { type: "denied" },
  },
];
